import type {
  CreativeLabelDefinition,
  CreativeLabelDisplay,
  CreativeLabelOption,
  CreativeLabelSchema,
  CreativeLabelType,
} from '../models/labelSchema.types'

export interface LabelColumn {
  field: string
  header: string
  sub_type: string
  description: string | null
  display?: CreativeLabelDisplay
  values: CreativeLabelOption[]
  is_label: true
}

export interface DropdownValues {
  unique_count: number
  values: unknown[]
}

export type CreativeDropdownables = Record<string, DropdownValues>

/** Localized fallbacks supplied by the UI boundary; services never own visible copy. */
export interface LabelBooleanCopy {
  trueText: string
  falseText: string
  yesText: string
  noText: string
}

export function canonicalLabelType(type: CreativeLabelType): string {
  switch (type) {
    case 'boolean':
      return 'yesno'
    case 'enum':
      return 'multiple'
    case 'string':
      return 'text'
    default:
      return type
  }
}

function normalizeItem(item: unknown): string {
  return String(item)
    .replace(/^['"]|['"]$/g, '')
    .trim()
}

/** Legacy-compatible value reader for arrays, single-quoted arrays, scalars and malformed lists. */
export function parseLabelValue(value: unknown): string[] {
  if (value === null || value === undefined || value === '' || value === '-') return []
  if (Array.isArray(value)) return value.map(normalizeItem)
  const source = String(value).trim()
  if (source.startsWith('[') && source.endsWith(']')) {
    try {
      const parsed: unknown = JSON.parse(source.replaceAll("'", '"'))
      if (Array.isArray(parsed)) return parsed.map(normalizeItem)
    } catch {
      return source.slice(1, -1).split(',').map(normalizeItem).filter(Boolean)
    }
  }
  return [normalizeItem(value)]
}

function displayValue(
  display: CreativeLabelDisplay | undefined,
  value: string,
  copy: Pick<LabelBooleanCopy, 'trueText' | 'falseText'>,
): string {
  if (value === 'true' || value === 'false') {
    const truthy = value === 'true'
    if (!display) return truthy ? copy.trueText : copy.falseText
    return truthy ? (display.true_text ?? copy.trueText) : (display.false_text ?? copy.falseText)
  }
  return display?.values?.[value] ?? value
}

/**
 * Legacy parity (`CreativeAiCard.vue`'s `formatSingleBadge` over `parseArrayValue`): one display
 * string per selected value, so a multi-value label renders as separate chips rather than one
 * joined string. Empty/`'-'` values yield an empty list — the caller decides what "no value" looks
 * like.
 */
export function labelValueTexts(
  definition: Pick<CreativeLabelDefinition, 'display'>,
  value: unknown,
  copy: Pick<LabelBooleanCopy, 'trueText' | 'falseText'>,
): string[] {
  return parseLabelValue(value).map((item) => displayValue(definition.display, item, copy))
}

export function labelValueText(
  definition: Pick<CreativeLabelDefinition, 'display'>,
  value: unknown,
  copy: Pick<LabelBooleanCopy, 'trueText' | 'falseText'>,
): string {
  const values = labelValueTexts(definition, value, copy)
  if (values.length === 0) return '-'
  return values.join(', ')
}

export function labelValueOptions(definition: CreativeLabelDefinition): CreativeLabelOption[] {
  return definition.values ?? definition.enum ?? []
}

/**
 * Legacy parity (`CreativeAiCard.vue`'s `getBadgeDescription`): the schema-authored description for
 * the nth selected value, looked up by its RAW value rather than its display text. `null` when the
 * schema defines no options, the index is out of range, or the option carries no description.
 */
export function labelOptionDescription(
  definition: CreativeLabelDefinition,
  value: unknown,
  index: number,
): string | null {
  const raw = parseLabelValue(value)[index]
  if (raw === undefined) return null
  return labelValueOptions(definition).find((option) => option.value === raw)?.description ?? null
}

/** Resolves a schema value for data surfaces that also carry option-level display text. */
export function labelOptionText(
  definition: CreativeLabelDefinition,
  value: unknown,
  copy: Pick<LabelBooleanCopy, 'trueText' | 'falseText'>,
): string {
  const values = parseLabelValue(value)
  if (values.length !== 1) return labelValueText(definition, value, copy)
  return (
    labelValueOptions(definition).find((option) => option.value === values[0])?.display_name ??
    labelValueText(definition, value, copy)
  )
}

/**
 * Legacy parity (`CreativeAiWorkspace.vue`'s `schemaCols`): report 13 exposes every custom label as
 * a `cu_`-prefixed column, while the label schema is keyed WITHOUT the prefix. Requesting the bare
 * key returns no such dimension, so the values come back empty and the real `cu_*` columns surface
 * as unrecognised raw fields instead of labels. Prefixing is idempotent — a key that already
 * carries it is left alone, exactly as old did.
 */
export const CREATIVE_LABEL_FIELD_PREFIX = 'cu_'

export function labelFieldFor(schemaKey: string): string {
  return schemaKey.startsWith(CREATIVE_LABEL_FIELD_PREFIX)
    ? schemaKey
    : `${CREATIVE_LABEL_FIELD_PREFIX}${schemaKey}`
}

/** The inverse lookup: resolve a schema definition from a wire field, prefixed or not. */
export function labelDefinitionFor(
  schema: CreativeLabelSchema,
  field: string,
): CreativeLabelDefinition | undefined {
  return (
    schema[field] ??
    (field.startsWith(CREATIVE_LABEL_FIELD_PREFIX)
      ? schema[field.slice(CREATIVE_LABEL_FIELD_PREFIX.length)]
      : undefined)
  )
}

export function stripLabelFieldPrefix(field: string): string {
  return field.startsWith(CREATIVE_LABEL_FIELD_PREFIX)
    ? field.slice(CREATIVE_LABEL_FIELD_PREFIX.length)
    : field
}

export function schemaToColumns(schema: CreativeLabelSchema): LabelColumn[] {
  return Object.entries(schema).map(([key, definition]) => ({
    field: labelFieldFor(key),
    header: definition.name,
    sub_type: canonicalLabelType(definition.type),
    description: definition.description ?? null,
    ...(definition.display ? { display: definition.display } : {}),
    values: labelValueOptions(definition),
    is_label: true,
  }))
}

export function enrichDropdownables(
  columns: readonly LabelColumn[],
  base: CreativeDropdownables,
  copy: Pick<LabelBooleanCopy, 'yesText' | 'noText'>,
): CreativeDropdownables {
  const enriched = { ...base }
  for (const column of columns) {
    const type = canonicalLabelType(column.sub_type)
    if (type === 'yesno') {
      enriched[column.field] = {
        unique_count: 2,
        values: [
          column.display?.true_text ?? copy.yesText,
          column.display?.false_text ?? copy.noText,
        ],
      }
      continue
    }
    const values = column.values.map((option) => option.display_name)
    if (values.length > 0) enriched[column.field] = { unique_count: values.length, values }
  }
  return enriched
}

export function mapDisplayFiltersToKeys<T extends { field: string; value: unknown }>(
  filters: readonly T[],
  columns: readonly LabelColumn[],
  copy: Pick<LabelBooleanCopy, 'yesText' | 'noText'>,
): T[] {
  const byField = new Map(columns.map((column) => [column.field, column]))
  return filters.map((filter) => {
    const column = byField.get(filter.field)
    if (!column) return filter
    const reverse = new Map<string, string>()
    if (canonicalLabelType(column.sub_type) === 'yesno') {
      reverse.set(column.display?.true_text ?? copy.yesText, 'true')
      reverse.set(column.display?.false_text ?? copy.noText, 'false')
    }
    for (const option of column.values) reverse.set(option.display_name, option.value)
    const mapOne = (value: unknown): unknown =>
      typeof value === 'string' ? (reverse.get(value) ?? value) : value
    const mapped = Array.isArray(filter.value) ? filter.value.map(mapOne) : mapOne(filter.value)
    return { ...filter, value: mapped }
  })
}
